// src/lib/production.js
// ───────────────────────────────────────────────────────────────────────────
// Lógica de producción compartida (impresión FRENTE/DORSO + corte). PURO/testeable.
// 2 caras: la impresión se cuenta por separado para el frente (printedCount) y el
// dorso (printedCountBack); 1 cara usa solo printedCount. El corte es uno solo.
// ───────────────────────────────────────────────────────────────────────────
import { deleteField } from 'firebase/firestore';

export const repsOf = (s) => Number(s?.repetitions) || 1;
export const printedFront = (s) => Number(s?.printedCount) || 0;
export const printedBack = (s) => Number(s?.printedCountBack) || 0;
export const cutOf = (s) => Number(s?.cutCount) || 0;
export const isTwoSided = (s) => !!s?.isPrinting && Number(s?.printFaces) === 2;
export const needsCut = (s) => s?.hasCut !== false;

/** Repeticiones COMPLETAMENTE impresas (listas para cortar). 2 caras: min(frente, dorso). */
export const printedDone = (s) => (isTwoSided(s) ? Math.min(printedFront(s), printedBack(s)) : printedFront(s));

/** ¿Impresión completa? 1 cara: frente ≥ reps. 2 caras: frente Y dorso ≥ reps. */
export function isFullyPrinted(s) {
  if (!s?.isPrinting) return false;
  const reps = repsOf(s);
  if (isTwoSided(s)) return printedFront(s) >= reps && printedBack(s) >= reps;
  return printedFront(s) >= reps;
}

/** ¿Producida del todo? (impresión completa si imprime + corte completo si lleva corte). */
export function isProduced(s) {
  const reps = repsOf(s);
  const printOk = !s?.isPrinting || isFullyPrinted(s);
  const cutOk = !needsCut(s) || cutOf(s) >= reps;
  return printOk && cutOk;
}

/** Estado de producción derivado (mismo criterio en PrepressTab y SheetDetailModal). */
export function computeProductionStatus(s) {
  const reps = repsOf(s);
  const cut = cutOf(s);
  const printed = isFullyPrinted(s);
  if (!needsCut(s) && s?.isPrinting && printed) return "Producido";
  if (needsCut(s) && cut >= reps) return s?.isPrinting ? "Producido" : "Cortada";
  if (s?.isPrinting && printed && cut > 0) return "Impresa y Cortada";
  if (!s?.isPrinting && cut > 0) return "Cortada";
  if (s?.isPrinting && printed) return "Impresa";
  return "";
}

/**
 * Campos que REINICIAN el ciclo de producción de una plancha. Fuente ÚNICA: los usan
 * las rutas que reportan una falla de producción (crean la tarea `system_reproduction`,
 * o sea el estado "Reproducir") y también el cierre de esa alerta al completar la tarea.
 *
 * Por qué existe: el estado "Reproducir" se DERIVA de que la tarea exista, así que
 * reiniciar los contadores solo al completarla dejaba toda la ventana de reproducción
 * con el contador del ciclo anterior — topado contra `repetitions` — y con los controles
 * de impresa/cortada deshabilitados. El operador no podía marcar nada.
 *
 * Incluye `printedCountBack` A PROPÓSITO: omitirlo dejaba el dorso topado para siempre
 * en las planchas de 2 caras (los otros dos contadores se liberaban y ese no).
 *
 * Es función y no constante: cada llamada necesita su propio sentinel de deleteField().
 */
export const productionResetFields = () => ({
  printedCount: 0,
  printedCountBack: 0,
  cutCount: 0,
  productionStatus: deleteField(),
});
